import React from 'react';
import { Button, Flex, Text } from '@optiaxiom/react';
import { IconPlus } from '@optimizely/axiom-icons';

import { FilterItem } from './FilterItem';
import { Filter, FilterType } from './types';

type FilterBuilderProps = {
  value: Filter[];
  onChange: (filters: Filter[]) => void;
  label: string;
  description?: string;
  error?: string;
};

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  value,
  onChange,
  label,
  description,
  error,
}) => {
  const handleAddFilter = () => {
    const newFilter: Filter = {
      type: FilterType.CONTAINS,
      value: '',
      case_sensitive: false,
    };
    onChange([...value, newFilter]);
  };

  const handleRemoveFilter = (index: number) => {
    const newFilters = value.filter((_, i) => i !== index);
    onChange(newFilters);
  };

  const handleFilterChange = (index: number, updates: Partial<Filter>) => {
    const newFilters = value.map((filter, i) => (i === index ? { ...filter, ...updates } : filter));
    onChange(newFilters);
  };

  return (
    <Flex flexDirection="column" gap="4">
      <Flex flexDirection="column" gap="2">
        <Text fontWeight="600" fontSize="sm">
          {label}
        </Text>
        {description && (
          <Text fontSize="sm" color="fg.secondary">
            {description}
          </Text>
        )}
      </Flex>

      <Flex
        flexDirection="column"
        gap="4"
        p="4"
        border="1"
        borderColor={error ? 'border.error' : 'border.default'}
        borderR="2"
      >
        {value.length > 0 && (
          <Flex flexDirection="column" gap="4">
            {value.map((filter, index) => (
              <FilterItem
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                filter={filter}
                index={index}
                onChange={handleFilterChange}
                onRemove={handleRemoveFilter}
              />
            ))}
          </Flex>
        )}

        {/* Empty State */}
        {value.length === 0 && (
          <Text fontSize="sm" color="fg.secondary">
            No filters configured. All items will be accepted.
          </Text>
        )}

        {/* Add Filter Button */}
        <Button appearance="subtle" icon={<IconPlus />} onClick={handleAddFilter} size="sm">
          Add Filter
        </Button>
      </Flex>

      {/* Error Message */}
      {error && (
        <Text fontSize="sm" color="fg.error">
          {error}
        </Text>
      )}

      {/* OR Logic Note */}
      {value.length > 1 && (
        <Text fontSize="xs" color="fg.secondary">
          Note: Items matching <strong>any one</strong> of these filters will be accepted (OR
          logic).
        </Text>
      )}
    </Flex>
  );
};
